import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { handleError } from '@opu/utils';

const useForm = (optionsToDisplay) => {
  const { addButton, editAction, deleteAction, searchEstablishment = false, bulkAction } = optionsToDisplay;

  const [listStudent, setListStudent] = useState([]);
  const [totalItems, setTotalItems] = useState(null);
  const [totalPages, setTotalPages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [establishments, setEstablishments] = useState([]);
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [query, setQuery] = useState({ page: null, order: '', keyword: '', limit: 10 });
  const [establishmentSearch, setEstablishmentSearch] = useState();
  const [studentIsActivatedSearch, setStudentIsActivatedSearch] = useState(null);
  const [region, setRegion] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get('page'));
    const order = queryParams.get('order');
    const keyword = queryParams.get('keyword');
    setQuery((prev) => ({
      ...prev,
      page: page ? page - 1 : 0,
      order: order ? order : query.order,
      keyword: keyword ? keyword : '',
    }));
  }, [location]);

  useEffect(() => {
    if (query.page !== null) {
      findAllStudents();
    }
  }, [query, establishmentSearch, studentIsActivatedSearch, region]);

  useEffect(() => {
    if (searchEstablishment) {
      getEstablishments();
    }
  }, []);

  const findAllStudents = () => {
    setLoading(true);
    AxiosInstance.get('/student', {
      params: { ...query, establishmentId: establishmentSearch, isActivated: studentIsActivatedSearch, region: region },
    })
      .then((response) => {
        setListStudent(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.totalItems);
        setLoading(false);
      })
      .catch((error) => {
        handleError(error);
        Swal.fire(t('student.ERROR_GET_STUDENTS'), '', 'error');
        setLoading(false);
      });
  };

  const updateStudent = (id) => {
    history.push({
      pathname: '/student/update',
      params: {
        id: id,
      },
    });
  };

  const getEstablishments = () => {
    setLoading(true);
    AxiosInstance.get('/establishment')
      .then((res) => {
        setEstablishments(res.data.items);
      })
      .catch((error) => {
        Swal.fire(t('establishment.ERROR_GET_ESTABLISHMENT'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteStudent = (id) => {
    Swal.fire({
      title: t('swal.are_you_sure'),
      text: t('swal.delete_permanently'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      cancelButtonText: t('swal.no'),
      confirmButtonText: t('swal.yes_delete'),
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        AxiosInstance.delete('/student/' + id)
          .then((response) => {
            Swal.fire(t('student.SUCCESS_DELETE_STUDENT'), '', 'success');
            findAllStudents();
          })
          .catch((error) => {
            Swal.fire(t('student.ERROR_DELETE_STUDENT'), '', 'error');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };
  const deleteAllStudents = (ids) => {
    Swal.fire({
      title: t('swal.are_you_sure'),
      text: t('swal.delete_permanently'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      cancelButtonText: t('swal.no'),
      confirmButtonText: t('swal.yes_delete'),
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        AxiosInstance.post('/student/delete-all', { ids: ids })
          .then((response) => {
            Swal.fire(t('student.SUCCESS_DELETE_STUDENTS'), '', 'success');
            findAllStudents();
          })
          .catch((error) => {
            Swal.fire(t('student.ERROR_DELETE_STUDENTS'), '', 'error');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const activeAllStudents = (ids) => {
    Swal.fire({
      title: t('swal.are_you_sure'),
      text: t('swal.student.update_status'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      cancelButtonText: t('swal.no'),
      confirmButtonText: t('swal.student.activate_status'),
    }).then((result) => {
      if (result.isConfirmed) {
        AxiosInstance.post('/student/activate-status-all', {
          ids: ids,
        })
          .then(() => {
            Swal.fire(t('student.SUCCESS_UPDATE_STUDENTS'), '', 'success');
            setListStudent([]);
            findAllStudents();
          })
          .catch((error) => {
            Swal.fire(t('student.ERROR_UPDATE_STUDENTS'), '', 'error');
          });
      }
    });
  };

  const disableAllStudents = (ids) => {
    Swal.fire({
      title: t('swal.are_you_sure'),
      text: t('swal.student.update_status'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      cancelButtonText: t('swal.no'),
      confirmButtonText: t('swal.student.disable_status'),
    }).then((result) => {
      if (result.isConfirmed) {
        AxiosInstance.post('/student/disable-status-all', {
          ids: ids,
        })
          .then(() => {
            Swal.fire(t('student.SUCCESS_UPDATE_STUDENTS'), '', 'success');
            setListStudent([]);
            findAllStudents();
          })
          .catch((error) => {
            Swal.fire(t('student.ERROR_UPDATE_STUDENTS'), '', 'error');
          });
      }
    });
  };
  const attributes = [
    { name: 'firstName', title: t('table.firstName'), nestedObject: ['user'], type: 'text', order: true },
    { name: 'lastName', title: t('table.lastName'), nestedObject: ['user'], type: 'text', order: true },
    { name: 'email', title: t('table.email'), nestedObject: ['user'], type: 'text', order: true },
    { name: 'gender', title: t('table.gender'), nestedObject: ['user'], type: 'text', order: true },
    { name: 'bac_matricule', title: t('table.matricule-bac'), nestedObject: ['user'], type: 'text', order: false },
    { name: 'phone', title: t('table.phone'), nestedObject: ['user'], type: 'text', order: false },
    { name: 'isActivated', title: t('table.isActivated'), nestedObject: [], type: 'isActivated', order: false },
    { name: 'creationDate', title: t('table.creationDate'), nestedObject: ['user'], type: 'date', order: true },
  ];

  const actions = {
    details: null,
    delete: deleteAction && deleteStudent,
    update: editAction && updateStudent,
    bulk: bulkAction && [
      {
        title: 'delete all',
        action: deleteAllStudents,
      },
      {
        title: 'activate all',
        action: activeAllStudents,
      },
      {
        title: 'disable all',
        action: disableAllStudents,
      },
    ],
  };

  return {
    attributes,
    actions,
    listStudent,
    findAllStudents,
    establishments,
    totalPages,
    loading,
    setLoading,
    query,
    establishmentSearch,
    setEstablishmentSearch,
    studentIsActivatedSearch,
    setStudentIsActivatedSearch,
    region,
    setRegion,
  };
};

export default useForm;
